import { create } from "zustand";
import { ClipSchema, deriveClip, defaultEdits, setCutTransition, rekeyTransition, pruneTransitions, healOverlays, freeSpot, DEFAULT_OVERLAY_MS, type CutStyle, findEmphasis, growToWord, mergeJobFields, openerHoldMs, restoreRange, sourceToTimeline, timelineToSource, type CaptionLang, type Clip, type Segment, type Word } from "@hudhud/core";
import { api, type ClipFiles, type ClipWithFiles, type LectureSummary, type Transcript } from "../api.js";
import type { GalleryKind } from "./fxCatalog.js";

export type SaveState = "saved" | "saving" | "error" | "idle";

export interface Layout { leftW: number; rightW: number; timelineH: number; leftOpen: boolean; rightOpen: boolean; timelineOpen: boolean }
export const DEFAULT_LAYOUT: Layout = { leftW: 360, rightW: 340, timelineH: 230, leftOpen: true, rightOpen: true, timelineOpen: true };
const loadLayout = (): Layout => { try { return { ...DEFAULT_LAYOUT, ...JSON.parse(localStorage.getItem("studio.layout") ?? "{}") }; } catch { return DEFAULT_LAYOUT; } };

/** Fills the editable primitives for clips made before the editor existed. */
export function migrateClip(clip: Clip, transcript: Transcript | null): Clip {
  let out = clip;
  if (!out.edits) out = { ...out, edits: defaultEdits(out.sourceStartMs, out.sourceEndMs) };
  if (!out.words.length && transcript) {
    const words = transcript.words.filter((w) => w.endMs > out.sourceStartMs - 3000 && w.startMs < out.sourceEndMs + 3000).map((w) => ({ ...w }));
    const hits = new Set(findEmphasis(words, out.selection?.emphasisWords ?? []).flatMap((h) => h.idx));
    out = { ...out, words: words.map((w, i) => (hits.has(i) ? { ...w, emphasis: true } : w)) };
  }
  if (!out.silences.length && out.segments.length > 1) {
    const silences = out.segments.slice(1).map((s, i) => ({ startMs: out.segments[i].outMs - 120, endMs: s.inMs + 120 }));
    out = { ...out, silences };
  }
  if (!out.facePoints.length && out.crop.length) {
    out = { ...out, facePoints: out.crop.filter((k) => k.zoom === 1).map((k) => ({ ms: k.ms, cx: k.cx, cy: k.cy, w: 0.1 })) };
  }
  return out;
}

interface EditorState {
  clip: Clip | null;
  files: ClipWithFiles["files"] | null;
  lecture: LectureSummary | null;
  /** what the preview plays; `fallback` = the whole lecture video while the lighter per-clip cut is still being made */
  media: { startMs: number; endMs: number; fallback?: boolean } | null;
  /** a different preview file arrived while the player was running: it is applied on the next pause, never mid-playback */
  pendingMedia: { mediaUrl: string | null; media: { startMs: number; endMs: number; fallback?: boolean } | null } | null;
  thumbs: { fps: number; cols: number; w: number; h: number; count: number; startMs: number; url: string } | null;
  transcript: Transcript | null;
  history: Clip[];
  future: Clip[];
  playheadMs: number;
  playing: boolean;
  loop: boolean;
  speed: number;
  safeZone: boolean;
  selection: { a: number; b: number } | null; // indices into effective words
  selectedPiece: number | null; // index into clip.segments
  /** the cut picked on the timeline (index = the piece before it): the transitions gallery then applies to it alone */
  selectedCut: number | null;
  /** a transition tile being dragged over the timeline: the cut it would land on lights up */
  cutDrag: { style: CutStyle; over: number | null } | null;
  snap: boolean;
  audition: "mix" | "music" | "voice";
  /** which inspector tab is open; the timeline switches it when a block is clicked */
  inspectorTab: string;
  /** a section the inspector should open next (set when the timeline or preview points at one), consumed by the panel */
  inspectorSection: string | null;
  /** the preview draws the render's full effects (the heavier composition) instead of the instant player; off each time the studio opens */
  exactPreview: boolean;
  /** what is selected on the video for direct editing: captions, the opener, the logo, a picture… (an edit kind) */
  editSel: string | null;
  setEditSel: (k: string | null) => void;
  /** bumps when the row under the preview asks for the text editor on the selected thing */
  textEditRequest: number;
  requestTextEdit: () => void;
  /** which gallery fills the settings column (or the phone's sheet): caption looks, transitions, colour looks, effects; null = none */
  gallery: GalleryKind | null;
  /** where a tapped effect goes in the sound library */
  soundTarget: "opener" | "endCard" | "cuts" | "playhead";
  setSoundTarget: (t: "opener" | "endCard" | "cuts" | "playhead") => void;
  /** the sound library, opened for one place */
  openSoundGallery: (t: "opener" | "endCard" | "cuts" | "playhead") => void;
  /** opens a gallery (and the settings column on the desk, so it can be seen); null closes whichever is open */
  openGallery: (kind: GalleryKind | null) => void;
  /** the caption looks gallery on or off: the older switch, kept for the callers that only know that one */
  setGalleryOpen: (v: boolean) => void;
  /** a copied piece (source in-point) waiting to be pasted into the timeline */
  clipboardPiece: number | null;
  /** language shown in the preview; the stored clip is always the English master */
  previewLang: CaptionLang;
  timelineZoom: number;
  layout: Layout;
  save: SaveState;
  error: string | null;
  load: (v: string, c: string) => Promise<void>;
  refreshFiles: () => Promise<void>;
  /** After a server job (translate, captions, analyse) wrote to the clip, take its results without losing local edits. */
  absorbServerFields: () => Promise<void>;
  update: (fn: (c: Clip) => Clip, opts?: { transient?: boolean }) => void;
  undo: () => void;
  redo: () => void;
  setPlayhead: (ms: number) => void;
  /** playhead from a pointer scrub: many events per frame collapse into one store write */
  scrubTo: (ms: number) => void;
  setPlaying: (p: boolean) => void;
  setLoop: (l: boolean) => void;
  setSpeed: (s: number) => void;
  setSafeZone: (s: boolean) => void;
  setSelection: (s: { a: number; b: number } | null) => void;
  setSelectedPiece: (i: number | null) => void;
  setSelectedCut: (i: number | null) => void;
  setCutDrag: (d: { style: CutStyle; over: number | null } | null) => void;
  /** one cut's own transition (null: back to the clip's style); the store keys it to the piece before the cut */
  setCutTransition: (i: number, style: CutStyle | null) => void;
  /** where a new picture goes: the selected words, else the paused playhead, else a free spot near the middle of the speech */
  newOverlayRange: () => { startMs: number; endMs: number; how: "selection" | "playhead" | "spot" } | null;
  setSnap: (v: boolean) => void;
  setAudition: (a: "mix" | "music" | "voice") => void;
  setPreviewLang: (l: CaptionLang) => void;
  setInspectorTab: (tab: string, section?: string | null) => void;
  clearInspectorSection: () => void;
  setExactPreview: (v: boolean) => void;
  // arrangement (playback order of the pieces)
  movePiece: (i: number, delta: number) => void;
  movePieceTo: (from: number, to: number) => void;
  pieceToStart: (i: number) => void;
  copyPiece: (i: number) => void;
  pastePiece: (after?: number) => void;
  duplicatePiece: (i: number) => void;
  resetArrangement: () => void;
  // opener / end card / audio from the timeline
  setEndDuration: (ms: number) => void;
  setMusicStart: (ms: number) => void;
  setMusicLevel: (db: number) => void;
  setVoiceLevel: (db: number) => void;
  /** BG audio silent over a source range (Quran, du'a) */
  addMusicMute: (inMs: number, outMs: number) => void;
  /** words behind the speaker over a source range (the selected words, say); returns the new item's id */
  addBehindText: (startMs: number, endMs: number, text?: string) => string;
  /** writes any pending edit now and resolves when the server has it */
  flushSave: () => Promise<void>;
  /** the reference picture being edited (timeline click → Images tab) */
  selectedOverlay: string | null;
  setSelectedOverlay: (id: string | null) => void;
  /** pulls the server-owned Quran scan into the open clip */
  reloadFromServer: () => Promise<void>;
  removeMusicMute: (index: number) => void;
  setTimelineZoom: (z: number) => void;
  setLayout: (patch: Partial<Layout>) => void;
  /** Brings a cut-off transcript word (and everything between it and the clip) back in, video included. */
  includeWord: (word: Word, side: "before" | "after") => void;
  /** Puts a cut source range back so its words play again. */
  addBack: (inMs: number, outMs: number) => void;
  // timeline edit operations (all undoable)
  playheadSourceMs: () => number | null;
  splitAtPlayhead: () => void;
  deletePiece: (i: number) => void;
  trimToPlayhead: (edge: "start" | "end") => void;
  toggleGapAtPlayhead: () => void;
  setGap: (i: number, range: { inMs: number; outMs: number } | null, opts?: { transient?: boolean }) => void;
  addZoomMarkAtPlayhead: () => void;
  removeZoomMark: (ms: number) => void;
  cardBreakAtPlayhead: () => void;
  selectCard: (cardIndex: number) => void;
  setHookDuration: (ms: number) => void;
  setHookDelay: (ms: number) => void;
  /** Move a directed beat (and its window) to a new source time. */
  moveMoment: (id: string, atMs: number) => void;
  /** Change one quiet range of the BG audio (source ms). */
  setMusicMute: (index: number, range: { inMs: number; outMs: number }) => void;
}

const SAVE_DEBOUNCE_MS = 600;
let saveTimer: ReturnType<typeof setTimeout> | null = null;
/** the server's pause settings: every derive in the editor uses the same numbers as the renders */
let deriveOpts: { gapMs: number; padMs: number; langFonts?: Record<string, string> } = { gapMs: 1000, padMs: 200 };
/** The studio's default font per language (from Settings), as the server sent it with the clip. */
export const langFontDefaults = (): Record<string, string> => deriveOpts.langFonts ?? {};
let pendingScrub: number | null = null;
/** the clip as it was before the current run of transient updates (a drag in motion), so the run undoes as one step */
let transientBase: Clip | null = null;
let scrubFrame: number | null = null;

export const useEditor = create<EditorState>((set, get) => {
  /** New file urls from the server. Everything applies at once except the preview media: while the player runs, a changed media file
   *  (the lighter cut landing, a re-cut after a trim) would reload every video element mid-play, so it waits for the next pause. */
  const mediaKey = (url: string | null | undefined, m: { startMs: number; endMs: number } | null | undefined) => `${url ?? ""}|${m?.startMs ?? ""}|${m?.endMs ?? ""}`;
  const takeFiles = (files: ClipFiles, media: EditorState["media"]) => {
    const cur = get();
    const changed = mediaKey(files.media, media) !== mediaKey(cur.files?.media, cur.media);
    if (changed && cur.playing && cur.files?.media && cur.media) {
      set({ files: { ...files, media: cur.files.media }, pendingMedia: { mediaUrl: files.media, media } });
    } else {
      set({ files, media, pendingMedia: null });
    }
  };
  const saveNow = async () => {
      saveTimer = null;
      const clip = get().clip;
      if (!clip) return;
      try {
        const r = await api.saveClip(clip);
        // the server may have merged newer job results (translations, covers, titles) into what we sent: take them, keep any edits made since
        const { files: _f, ...raw } = r.clip;
        const srv = ClipSchema.parse(raw);
        const cur = get().clip;
        if (cur && cur.id === srv.id) { const merged = mergeJobFields(srv, cur); if (merged !== cur && JSON.stringify(merged.stamps) !== JSON.stringify(cur.stamps)) set({ clip: deriveClip({ ...merged, status: srv.status }, deriveOpts) }); }
        // media comes back null when the clip grew past the preview file; the editor polls until the re-cut lands
        set({ save: "saved" });
        takeFiles(r.clip.files, r.media !== undefined ? r.media : get().media);
      } catch (e: any) {
        set({ save: "error", error: e.message });
      }
  };
  const scheduleSave = () => {
    if (saveTimer) clearTimeout(saveTimer);
    set({ save: "saving" });
    saveTimer = setTimeout(saveNow, SAVE_DEBOUNCE_MS);
  };
  const flushSave = async () => { if (saveTimer) { clearTimeout(saveTimer); await saveNow(); } };

  return {
    clip: null, files: null, lecture: null, media: null, pendingMedia: null, thumbs: null, transcript: null, history: [], future: [],
    playheadMs: 0, playing: false, loop: false, speed: 1, safeZone: false, selection: null, selectedPiece: null, selectedCut: null, cutDrag: null, snap: true, audition: "mix", inspectorTab: "words", inspectorSection: null, exactPreview: false, editSel: null, textEditRequest: 0, gallery: null, clipboardPiece: null, previewLang: "en", timelineZoom: 1, layout: loadLayout(), save: "idle", error: null,
    async load(v, c) {
      set({ clip: null, error: null, history: [], future: [], playheadMs: 0, selection: null, previewLang: "en" });
      const [{ clip, lecture, media, thumbs, derive }, lec] = await Promise.all([api.clip(v, c), api.lecture(v)]);
      if (derive) deriveOpts = derive;
      const { files, ...raw } = clip;
      const migrated = migrateClip(ClipSchema.parse(raw), lec.transcriptEn);
      const derived = deriveClip(migrated, deriveOpts);
      set({ clip: derived, files, lecture, media, thumbs: thumbs && files.thumbs ? { ...thumbs, url: `${files.thumbs}?v=${thumbs.startMs}_${thumbs.count}` } : null, transcript: lec.transcriptEn, save: "saved" });
      if (JSON.stringify(derived) !== JSON.stringify(raw)) scheduleSave();
    },
    async refreshFiles() {
      const clip = get().clip;
      if (!clip) return;
      const r = await api.clip(clip.videoId, clip.id);
      takeFiles(r.clip.files, r.media);
      set({ thumbs: r.thumbs && r.clip.files.thumbs ? { ...r.thumbs, url: `${r.clip.files.thumbs}?v=${r.thumbs.startMs}_${r.thumbs.count}` } : null });
    },
    async absorbServerFields() {
      const local = get().clip;
      if (!local) return;
      const r = await api.clip(local.videoId, local.id);
      const { files, ...raw } = r.clip;
      const srv = ClipSchema.parse(raw);
      const cur = get().clip;
      if (!cur || cur.id !== srv.id) return;
      const merged = deriveClip({ ...mergeJobFields(srv, cur), layout: srv.layout, status: srv.status }, deriveOpts);
      set({ clip: merged, thumbs: r.thumbs && files.thumbs ? { ...r.thumbs, url: `${files.thumbs}?v=${r.thumbs.startMs}_${r.thumbs.count}` } : null });
      takeFiles(files, r.media);
    },
    update(fn, opts) {
      const clip = get().clip;
      if (!clip) return;
      // a cut that vanished takes its own transition with it; a picture left outside the kept footage comes back to the nearest kept spot
      const next = healOverlays(pruneTransitions(deriveClip(fn(clip), deriveOpts)));
      if (opts?.transient) {
        // a drag or a slider in motion: no history yet, but remember where it started so the whole motion undoes as one step
        if (!transientBase) transientBase = clip;
        set({ clip: next });
      } else {
        const base = transientBase ?? clip;
        transientBase = null;
        set({ clip: next, history: [...get().history.slice(-99), base], future: [] });
      }
      scheduleSave();
    },
    undo() {
      transientBase = null;
      const { history, clip } = get();
      if (!history.length || !clip) return;
      const prev = history[history.length - 1];
      set({ clip: prev, history: history.slice(0, -1), future: [clip, ...get().future] });
      scheduleSave();
    },
    redo() {
      transientBase = null;
      const { future, clip } = get();
      if (!future.length || !clip) return;
      const [next, ...rest] = future;
      set({ clip: next, future: rest, history: [...get().history, clip] });
      scheduleSave();
    },
    setPlayhead: (ms) => { if (Math.abs(ms - get().playheadMs) >= 0.5) set({ playheadMs: ms }); },
    scrubTo: (ms) => {
      pendingScrub = ms;
      if (scrubFrame != null) return;
      scrubFrame = requestAnimationFrame(() => { scrubFrame = null; const v = pendingScrub; pendingScrub = null; if (v != null) get().setPlayhead(v); });
    },
    setPlaying: (playing) => {
      const p = get().pendingMedia;
      if (!playing && p) set({ playing, files: get().files ? { ...get().files!, media: p.mediaUrl } : get().files, media: p.media, pendingMedia: null });
      else set({ playing });
    },
    setLoop: (loop) => set({ loop }),
    setSpeed: (speed) => set({ speed }),
    setSafeZone: (safeZone) => set({ safeZone }),
    setSelection: (selection) => set({ selection, selectedPiece: selection ? null : get().selectedPiece }),
    setSelectedPiece: (selectedPiece) => set({ selectedPiece, selection: selectedPiece != null ? null : get().selection }),
    setSelectedCut: (selectedCut) => { if (selectedCut !== get().selectedCut) set({ selectedCut }); },
    setCutDrag: (cutDrag) => set({ cutDrag }),
    setCutTransition: (i, style) => get().update((c) => setCutTransition(c, i, style)),
    newOverlayRange: () => {
      const c = get().clip;
      if (!c) return null;
      const sel = clipHelpers.selectedRange(c, get().selection);
      if (sel) return { startMs: sel.startMs - 200, endMs: sel.endMs + 300, how: "selection" };
      const src = get().playheadSourceMs();
      if (src != null && !get().playing) { const seg = c.segments.find((sg) => src >= sg.inMs && src < sg.outMs); const startMs = Math.round(src); return { startMs, endMs: Math.max(startMs + 500, Math.min(seg ? seg.outMs : startMs + DEFAULT_OVERLAY_MS, startMs + DEFAULT_OVERLAY_MS)), how: "playhead" }; }
      const spot = freeSpot(c.segments, c.overlays, DEFAULT_OVERLAY_MS);
      return spot ? { ...spot, how: "spot" } : null;
    },
    setSnap: (snap) => set({ snap }),
    setAudition: (audition) => set({ audition }),
    setPreviewLang: (previewLang) => set({ previewLang }),
    setInspectorTab: (inspectorTab, section) => set({ inspectorTab, inspectorSection: section ?? null }),
    clearInspectorSection: () => set({ inspectorSection: null }),
    setExactPreview: (exactPreview) => set({ exactPreview }),
    setEditSel: (editSel) => set({ editSel }),
    requestTextEdit: () => set({ textEditRequest: get().textEditRequest + 1 }),
    openGallery: (kind) => { if (kind && !get().layout.rightOpen) get().setLayout({ rightOpen: true }); set({ gallery: kind }); },
    soundTarget: "opener",
    setSoundTarget: (t) => set({ soundTarget: t }),
    openSoundGallery: (t) => { set({ soundTarget: t }); get().openGallery("sound"); },
    setGalleryOpen: (v) => get().openGallery(v ? "captions" : null),
    movePiece(i, delta) { const c = get().clip; if (!c) return; get().movePieceTo(i, Math.max(0, Math.min(c.segments.length - 1, i + delta))); },
    movePieceTo(from, to) {
      const c = get().clip;
      if (!c || from === to || !c.segments[from] || to < 0 || to >= c.segments.length) return;
      const order = c.segments.map((s) => s.inMs);
      const [moved] = order.splice(from, 1);
      order.splice(to, 0, moved);
      get().update((cl) => ({ ...cl, edits: { ...cl.edits!, arrangement: order } }));
      set({ selectedPiece: to });
    },
    pieceToStart(i) { get().movePieceTo(i, 0); },
    copyPiece(i) { const c = get().clip; if (c?.segments[i]) set({ clipboardPiece: c.segments[i].inMs }); },
    pastePiece(after) {
      const c = get().clip;
      const ms = get().clipboardPiece;
      if (!c || ms == null) return;
      const order = c.segments.map((s) => s.inMs);
      const at = after == null ? order.length : Math.min(order.length, after + 1);
      order.splice(at, 0, ms);
      get().update((cl) => ({ ...cl, edits: { ...cl.edits!, arrangement: order } }));
      set({ selectedPiece: at });
    },
    duplicatePiece(i) { get().copyPiece(i); get().pastePiece(i); },
    resetArrangement() { get().update((cl) => ({ ...cl, edits: { ...cl.edits!, arrangement: [] } })); },
    setEndDuration(ms) { get().update((cl) => ({ ...cl, endCard: { ...cl.endCard, durationMs: Math.max(800, Math.min(6000, Math.round(ms))) } }), { transient: true }); },
    setMusicStart(ms) { get().update((cl) => ({ ...cl, music: { ...cl.music, startMs: Math.max(0, Math.round(ms)), endMs: cl.music.endMs == null ? null : Math.max(Math.round(ms) + 1000, cl.music.endMs + (Math.round(ms) - cl.music.startMs)) } }), { transient: true }); },
    setMusicLevel(db) { get().update((cl) => ({ ...cl, music: { ...cl.music, volumeDb: Math.max(-40, Math.min(-4, Math.round(db))) } }), { transient: true }); },
    setVoiceLevel(db) { get().update((cl) => ({ ...cl, voiceDb: Math.max(-24, Math.min(12, Math.round(db))) }), { transient: true }); },
    flushSave,
    selectedOverlay: null,
    setSelectedOverlay: (selectedOverlay) => set({ selectedOverlay }),
    async reloadFromServer() {
      const cur = get().clip;
      if (!cur) return;
      const r = await api.clip(cur.videoId, cur.id);
      const { files: _f, ...raw } = r.clip;
      const srv = ClipSchema.parse(raw);
      const now = get().clip;
      if (now && now.id === srv.id) set({ clip: deriveClip(mergeJobFields(srv, now), deriveOpts) });
    },
    addBehindText(startMs, endMs, text = "") {
      const id = `bt-${Date.now().toString(36)}${Math.floor(Math.random() * 1296).toString(36).padStart(2, "0")}`;
      const item = { id, text, startMs: Math.round(startMs), endMs: Math.round(endMs), font: null, size: 190, color: "#F7F5EF", accentColor: "#F5C542", opacity: 0.85, uppercase: true, animation: "rise" as const, offsetX: 0, offsetY: 0, enabled: true };
      get().update((cl) => ({ ...cl, behindTexts: [...cl.behindTexts, item].sort((a, b) => a.startMs - b.startMs) }));
      return id;
    },
    addMusicMute(inMs, outMs) { get().update((cl) => ({ ...cl, edits: { ...cl.edits!, musicMutes: [...cl.edits!.musicMutes, { inMs: Math.round(Math.min(inMs, outMs)), outMs: Math.round(Math.max(inMs, outMs)) }] } })); set({ selection: null }); },
    removeMusicMute(index) { get().update((cl) => ({ ...cl, edits: { ...cl.edits!, musicMutes: cl.edits!.musicMutes.filter((_, i) => i !== index) } })); },
    setTimelineZoom: (timelineZoom) => set({ timelineZoom: Math.max(0.5, Math.min(8, timelineZoom)) }),
    setLayout(patch) {
      const layout = { ...get().layout, ...patch };
      set({ layout });
      try { localStorage.setItem("studio.layout", JSON.stringify(layout)); } catch { /* ignore */ }
    },
    playheadSourceMs() {
      const c = get().clip;
      if (!c) return null;
      const videoMs = get().playheadMs - openerHoldMs(c); // the player clock includes a full-screen opener before the speech
      if (videoMs < 0) return null;
      return timelineToSource(c.segments, videoMs);
    },
    splitAtPlayhead() {
      const src = get().playheadSourceMs();
      const c = get().clip;
      if (src == null || !c) return;
      const ms = Math.round(src);
      const inside = c.segments.some((s) => ms > s.inMs + 40 && ms < s.outMs - 40);
      if (!inside) return;
      get().update((cl) => ({ ...cl, edits: { ...cl.edits!, splits: [...cl.edits!.splits, ms] } }));
      set({ selectedPiece: null });
    },
    deletePiece(i) {
      const c = get().clip;
      if (!c || !c.segments[i]) return;
      const seg = c.segments[i];
      const copies = c.segments.filter((s) => s.inMs === seg.inMs && s.outMs === seg.outMs).length;
      if (copies > 1) {
        // a repeated piece: drop just this occurrence from the playback order
        const order = c.segments.map((s) => s.inMs); order.splice(i, 1);
        get().update((cl) => ({ ...cl, edits: { ...cl.edits!, arrangement: order } }));
        set({ selectedPiece: null });
        return;
      }
      get().update((cl) => {
        const e = cl.edits!;
        if (i === 0) return { ...cl, edits: { ...e, trimStartMs: seg.outMs } };
        if (i === cl.segments.length - 1) return { ...cl, edits: { ...e, trimEndMs: seg.inMs } };
        return { ...cl, edits: { ...e, manualCuts: [...e.manualCuts, { inMs: seg.inMs, outMs: seg.outMs }] } };
      });
      set({ selectedPiece: null });
    },
    trimToPlayhead(edge) {
      const src = get().playheadSourceMs();
      if (src == null) return;
      get().update((cl) => ({ ...cl, edits: edge === "start" ? { ...cl.edits!, trimStartMs: Math.min(Math.round(src), cl.edits!.trimEndMs - 2000) } : { ...cl.edits!, trimEndMs: Math.max(Math.round(src), cl.edits!.trimStartMs + 2000) } }));
    },
    toggleGapAtPlayhead() {
      const c = get().clip;
      const src = get().playheadSourceMs();
      if (!c || src == null) return;
      // nearest boundary between two pieces
      let best: { i: number; d: number } | null = null;
      for (let i = 0; i < c.segments.length - 1; i++) { const d = Math.min(Math.abs(c.segments[i].outMs - src), Math.abs(c.segments[i + 1].inMs - src)); if (!best || d < best.d) best = { i, d }; }
      if (!best || best.d > 1500) return;
      const g = { inMs: c.segments[best.i].outMs, outMs: c.segments[best.i + 1].inMs };
      if (g.outMs - g.inMs < 40) return; // a split, not a gap
      get().setGap(best.i, null);
    },
    setGap(i, range, opts) {
      const c = get().clip;
      if (!c || !c.segments[i] || !c.segments[i + 1]) return;
      const g = { inMs: c.segments[i].outMs, outMs: c.segments[i + 1].inMs };
      get().update((cl) => {
        const e = cl.edits!;
        const manualCuts = e.manualCuts.filter((m) => !(m.inMs < g.outMs && m.outMs > g.inMs));
        const keptGaps = [...e.keptGaps, g];
        const splits = e.splits.filter((ms) => ms < g.inMs || ms > g.outMs);
        if (range && range.outMs - range.inMs >= 40) manualCuts.push(range);
        // the cut moved with the trim: its own transition moves with it
        const out = { ...cl, edits: { ...e, manualCuts, keptGaps, splits } };
        return range && range.outMs - range.inMs >= 40 ? rekeyTransition(out, g.inMs, range.inMs) : out;
      }, opts);
    },
    addZoomMarkAtPlayhead() {
      const src = get().playheadSourceMs();
      if (src == null) return;
      const ms = Math.round(src);
      get().update((cl) => ({ ...cl, edits: { ...cl.edits!, zoomMarks: cl.edits!.zoomMarks.some((m) => Math.abs(m - ms) < 300) ? cl.edits!.zoomMarks : [...cl.edits!.zoomMarks, ms] } }));
    },
    removeZoomMark(ms) { get().update((cl) => ({ ...cl, edits: { ...cl.edits!, zoomMarks: cl.edits!.zoomMarks.filter((m) => m !== ms) } })); },
    cardBreakAtPlayhead() {
      const c = get().clip;
      if (!c) return;
      const ph = get().playheadMs - openerHoldMs(c);
      const words = clipHelpers.words(c).map((w) => ({ w, t: sourceToTimeline(c.segments, w.startMs) })).filter((x) => x.t != null) as { w: Word; t: number }[];
      const next = words.find((x) => x.t >= ph - 60);
      if (!next) return;
      const t = Math.round(next.t);
      get().update((cl) => ({ ...cl, edits: { ...cl.edits!, cardBreaks: cl.edits!.cardBreaks.includes(t) ? cl.edits!.cardBreaks.filter((x) => x !== t) : [...cl.edits!.cardBreaks, t] } }));
    },
    selectCard(cardIndex) {
      const c = get().clip;
      const card = c?.captions[cardIndex];
      if (!c || !card) return;
      const words = clipHelpers.words(c);
      const starts = new Set(card.words.map((w) => w.startMs));
      const idx = words.map((w, i) => ({ i, t: sourceToTimeline(c.segments, w.startMs) })).filter((x) => x.t != null && starts.has(Math.round(x.t as number))).map((x) => x.i);
      if (idx.length) { set({ selection: { a: idx[0], b: idx[idx.length - 1] }, selectedPiece: null, playing: false, playheadMs: card.startMs + openerHoldMs(c) }); }
    },
    setHookDuration(ms) { get().update((cl) => ({ ...cl, hook: { ...cl.hook, durationMs: Math.max(600, Math.min(6000, Math.round(ms))) } }), { transient: true }); },
    setHookDelay(ms) { get().update((cl) => ({ ...cl, hook: { ...cl.hook, delayMs: Math.max(0, Math.min(3000, Math.round(ms))) } }), { transient: true }); },
    moveMoment(id, atMs) {
      get().update((cl) => ({ ...cl, direction: { ...cl.direction, moments: cl.direction.moments.map((m) => (m.id === id ? { ...m, atMs: Math.round(atMs), endMs: m.endMs == null ? null : Math.round(atMs + (m.endMs - m.atMs)) } : m)).sort((a, b) => a.atMs - b.atMs), from: cl.direction.from === "none" ? "manual" : cl.direction.from } }), { transient: true });
    },
    setMusicMute(index, range) {
      get().update((cl) => ({ ...cl, edits: { ...cl.edits!, musicMutes: cl.edits!.musicMutes.map((m, i) => (i === index ? { inMs: Math.round(Math.min(range.inMs, range.outMs - 200)), outMs: Math.round(Math.max(range.outMs, range.inMs + 200)) } : m)) } }), { transient: true });
    },
    includeWord(word, side) {
      const { transcript } = get();
      get().update((c) => growToWord(c, transcript?.words ?? null, word, side));
      const c = get().clip;
      const t = c ? sourceToTimeline(c.segments, side === "before" ? word.startMs : Math.max(word.startMs, c.edits!.trimStartMs)) : null;
      if (t != null && c) set({ playing: false, playheadMs: t + openerHoldMs(c), selection: null });
    },
    addBack(inMs, outMs) {
      get().update((c) => restoreRange(c, inMs, outMs));
      const c = get().clip;
      const t = c ? sourceToTimeline(c.segments, inMs + 20) : null;
      if (t != null && c) set({ playing: false, playheadMs: t + openerHoldMs(c), selection: null });
    },
  };
});

/** Helpers shared by the panes. */
export const clipHelpers = {
  /** the source range and text of the transcript selection (indices into the effective words) */
  selectedRange(clip: Clip, selection: { a: number; b: number } | null): { startMs: number; endMs: number; text: string } | null {
    if (!selection) return null;
    const ws = clipHelpers.words(clip);
    const a = Math.min(selection.a, selection.b), b = Math.max(selection.a, selection.b);
    const sel = ws.slice(a, b + 1);
    if (!sel.length) return null;
    return { startMs: sel[0].startMs, endMs: sel[sel.length - 1].endMs, text: sel.map((w) => w.text).join(" ") };
  },
  words(clip: Clip): Word[] {
    const { trimStartMs, trimEndMs, wordText } = clip.edits!;
    return clip.words.filter((w) => w.endMs > trimStartMs && w.startMs < trimEndMs).map((w) => (wordText[String(w.startMs)] !== undefined ? { ...w, text: wordText[String(w.startMs)] } : w));
  },
  isCut(clip: Clip, w: Word): boolean {
    return !clip.segments.some((s) => w.startMs < s.outMs && w.endMs > s.inMs);
  },
  gapsAfter(clip: Clip, w: Word, next: Word | undefined): Segment | null {
    if (!next) return null;
    const gap = next.startMs - w.endMs;
    if (gap < 400) return null;
    return { inMs: w.endMs, outMs: next.startMs };
  },
  gapIsCut(clip: Clip, gap: Segment): boolean {
    const mid = (gap.inMs + gap.outMs) / 2;
    return !clip.segments.some((s) => mid >= s.inMs && mid <= s.outMs);
  },
};
